import { LauncherState } from "./slices/launcherSlice";
import { RootState } from "./store";
import { FrameSettings } from "./types/core";

// NOTE(liamvdv): MUST NOT (transitively) IMPORT FROM "./sdk"

export function urlSafeEncode(input: object): string {
  const jsonString = JSON.stringify(input);
  // Convert to base64 using btoa
  const base64 = btoa(encodeURIComponent(jsonString));
  // URL-safe
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const urlSafeDecode = (str: string): object | null => {
  try {
    // Replace URL-safe characters back to standard Base64 characters
    const base64 = str.replace(/-/g, "+").replace(/_/g, "/");

    // Add padding if needed
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;

    // Decode base64 and handle UTF-8
    const decodedString = decodeURIComponent(atob(padded));
    return JSON.parse(decodedString);
  } catch (error) {
    console.error("Error decoding base64 JSON:", error);
    return null;
  }
};

export interface StateJson {
  frameId?: string;
  frameSettings: FrameSettings;
  launcherState: LauncherState;
  theme?: "auto" | "light" | "dark";
  locale?: string;
  apiBase?: string;
}

export function stateJsonToState(stateJson: StateJson): RootState {
  const isPromptDisabled = stateJson.frameId === undefined;
  const frameId = stateJson.frameId || "-".repeat(36);
  const frameSettings = stateJson.frameSettings || {
    assistant_name: "AI Assistant",
    themes: [
      {
        name: "light",
        brand: "#007AFF",
        header_bg: "#ffffff",
        chat: "#f8f9fa",
        chat_text: "#212529",
        chat_text_light: "#6c757d",
      },
      {
        name: "dark",
        brand: "#007AFF",
        header_bg: "#ffffff",
        chat: "#f8f9fa",
        chat_text: "#212529",
        chat_text_light: "#6c757d",
      },
    ],
    locales: [
      {
        title: "English",
        locale: "en",
        messages: [
          {
            role: "assistant",
            content: "Hello! How can I help you today?",
          },
        ],
        suggestions: ["Help", "Support", "Get Started"],
      },
    ],
    assistant_image_url: "https://example.com/image.png",
    header_image_url: "https://example.com/header.png",
    terms_of_service_url: "https://example.com/terms",
    privacy_policy_url: "https://example.com/privacy",
    show_sources: false,
    show_popup: true,
    allow_image_upload: true,
    launcher_settings: {
      button_color: "#007AFF",
      alignment: "right",
      side_margin: "20px",
      bottom_margin: "20px",
    },
  };
  const launcherState = stateJson.launcherState || {
    isShow: true,
    isOpen: false,
    lastOpen: 0,
    openedOnce: false,
    isPreviewVisible: false,
  };
  const theme = stateJson.theme || "auto";
  const locale = stateJson.locale || "en";
  const apiBase = stateJson.apiBase ?? import.meta.env.VITE_API_ENDPOINT_URL;
  return {
    config: {
      data: {
        frameId: frameId,
        apiBase: apiBase,
        cookieDomain: "",
        sessionRefreshLifetime: 3600,
        theme: theme,
        locale: locale,
        mode: "launcher",
        selector: "",
        whatsappId: "",
        previewDelayMs: 3000,
      },
      status: "succeeded",
      error: null,
    },
    user: {
      data: {
        user_data: {
          // NOTE(memben): we authenticate iframe via Clerk from Platform
          // first_name: "Platform",
          // last_name: "Editor",
          // email: "platform.editor@test.botbrains.io",
          // phone: "+14155552671",
          // external_id: undefined,
          // external_attributes: {
          //   birthday: "1990-01-01",
          // },
          // email_signature: undefined,
          // phone_signature: undefined,
          // external_id_signature: undefined,
        },
      },
      status: "succeeded",
      error: null,
    },
    frame: {
      data: {
        [frameId]: frameSettings,
      },
      status: {
        [frameId]: "succeeded",
      },
      backgroundStatus: {
        [frameId]: "succeeded",
      },
      error: {
        [frameId]: null,
      },
    },
    launcher: launcherState,
    challenge: {
      data: {
        [frameId]: {
          headers: {},
          name: "none",
          message: "",
          is_pass: true,
        },
      },
    },
    opengraph: {
      data: {},
      status: {},
      error: {},
    },
    prompt: {
      disabled: isPromptDisabled,
      text: "",
      show: true,
      suggestions: undefined,
      files: [],
    },
    signal: {
      active: {},
    },
    chat: {
      messages: [],
      conversationId: "",
      isLoading: false,
      apiError: "",
    },
    conversations: {
      data: [],
      status: "idle",
      error: null,
    },
    handoff: {
      data: {},
      extraData: {},
      status: {},
      error: {},
    },
  };
}
